import { motion, useReducedMotion } from 'framer-motion'
import { Briefcase, Target, TrendingDown, TrendingUp } from 'lucide-react'
import { OperacaoPipelineHeroCard } from '@/components/Dashboard/OperacaoPipelineHeroCard'
import {
  OPERACAO_METRIC_ACCENTS,
  OperacaoMetricCard,
} from '@/components/Dashboard/OperacaoMetricCard'
import { getDashboardVariants } from '@/components/Dashboard/dashboardMotion'
import {
  formatWinRateDisplay,
  groupDealsByJourneyMacro,
  type OperacaoSummaryView,
} from '@/components/Dashboard/operacaoTab.utils'
import {
  formatDashboardCurrency,
  formatDashboardNumber,
} from '@/utils/crmValuesDisplay'
import { seriesHasVariation, trendChangePercent } from '@/utils/kpiTrend'

type MonthlyPoint = { month: string; count: number; revenue: number }

type Props = {
  summary: OperacaoSummaryView
  monthlyTrend: MonthlyPoint[]
  valuesVisible?: boolean
}

export function OperacaoKpiGrid({ summary, monthlyTrend, valuesVisible = false }: Props) {
  const reduced = useReducedMotion()
  const v = getDashboardVariants(reduced)
  const winRate = formatWinRateDisplay(summary.winRate, summary.closedCount, valuesVisible)
  const journeySteps = groupDealsByJourneyMacro(summary.dealsByStage)

  const revenueSeries = monthlyTrend.map((m) => m.revenue)
  const revenueTrendDelta = trendChangePercent(revenueSeries)

  const secondaryCards = [
    {
      key: 'closed',
      label: 'Fechados no período',
      value: formatDashboardCurrency(summary.wonRevenue, valuesVisible),
      numericValue: summary.wonRevenue,
      valueFormat: 'currency' as const,
      subtitle: `${summary.wonDeals} deals ganhos`,
      trendDelta:
        seriesHasVariation(revenueSeries) ? revenueTrendDelta : null,
      trendPositive: summary.wonDeals > 0,
      icon: <Target size={16} />,
      accent: OPERACAO_METRIC_ACCENTS.closed,
    },
    {
      key: 'win-rate',
      label: 'Win rate',
      value: winRate.value,
      numericValue: summary.winRate,
      valueFormat: 'percent' as const,
      subtitle: winRate.hint ?? 'Taxa de fechamento',
      trendDelta: null,
      trendPositive: summary.winRate >= 50,
      icon: <TrendingUp size={16} />,
      accent: OPERACAO_METRIC_ACCENTS.winRate,
    },
    {
      key: 'active',
      label: 'Em negociação',
      value: formatDashboardNumber(summary.pipelineCount, valuesVisible),
      numericValue: summary.pipelineCount,
      valueFormat: 'integer' as const,
      subtitle: 'Deals ativos no funil',
      trendDelta: null,
      trendPositive: summary.pipelineCount > 0,
      icon: <Briefcase size={16} />,
      accent: OPERACAO_METRIC_ACCENTS.active,
    },
    {
      key: 'lost',
      label: 'Perdas no período',
      value: formatDashboardNumber(summary.lostDeals, valuesVisible),
      numericValue: summary.lostDeals,
      valueFormat: 'integer' as const,
      subtitle:
        summary.closedCount > 0
          ? `${formatDashboardNumber(summary.closedCount, valuesVisible)} fechamentos totais`
          : 'Sem fechamentos no período',
      trendDelta: null,
      trendPositive: summary.lostDeals === 0,
      icon: <TrendingDown size={16} />,
      accent: {
        bg: '#fee2e2',
        text: '#dc2626',
        ring: '#fca5a5',
      },
    },
  ]

  return (
    <motion.div
      variants={v.kpiGrid}
      initial='hidden'
      animate='show'
      className='grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 xl:grid-rows-2'
    >
      <motion.div variants={v.kpiItem} className='sm:col-span-2 xl:row-span-2'>
        <OperacaoPipelineHeroCard
          pipelineValue={summary.pipelineValue}
          pipelineCount={summary.pipelineCount}
          journeySteps={journeySteps}
          valuesVisible={valuesVisible}
        />
      </motion.div>

      {secondaryCards.map((kpi, index) => (
        <motion.div key={kpi.key} variants={v.kpiItem} className='min-h-[118px]'>
          <OperacaoMetricCard
            label={kpi.label}
            value={kpi.value}
            numericValue={kpi.numericValue}
            valueFormat={kpi.valueFormat}
            subtitle={kpi.subtitle}
            trendDelta={kpi.trendDelta}
            trendPositive={kpi.trendPositive}
            icon={kpi.icon}
            accent={kpi.accent}
            valuesVisible={valuesVisible}
            animationDelay={(index + 1) * 90}
            variant='compact'
          />
        </motion.div>
      ))}
    </motion.div>
  )
}
